import { useState, useRef, useEffect } from "react";

export default function useNotificationSound() {
    const [isPlaying, setIsPlaying] = useState(false);
    const [error, setError] = useState(null);
    const audioContextRef = useRef(null);

    useEffect(() => {
        // Initialize AudioContext on mount
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();

        return () => {
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, []);

    const createTone = (frequency, startTime, duration) => {
        if (!audioContextRef.current) return;

        const oscillator = audioContextRef.current.createOscillator();
        const gainNode = audioContextRef.current.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, startTime);

        // Set initial volume
        gainNode.gain.setValueAtTime(0.7, startTime);
        // Create fade out
        gainNode.gain.exponentialRampToValueAtTime(
            0.00001,
            startTime + duration
        );

        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
    };

    const play = async () => {
        try {
            if (isPlaying) return;
            setIsPlaying(true);

            const ctx = audioContextRef.current;
            const now = ctx.currentTime;
            const toneDuration = 0.15; // 150ms per tone
            const spacing = 0.05; // 50ms between tones

            // Medium tone (440Hz)
            createTone(440, now, toneDuration);
            
            // Low tone (330Hz)
            createTone(330, now + toneDuration + spacing, toneDuration);
            
            // High tone (660Hz)
            createTone(660, now + (toneDuration + spacing) * 2, toneDuration);

            // Reset playing state after all tones finish
            setTimeout(() => {
                setIsPlaying(false);
            }, (toneDuration * 3 + spacing * 2) * 1000 + 100); // Add small buffer

        } catch (err) {
            setError('Error playing notification sound');
            console.error(err);
            setIsPlaying(false);
        }
    };

    const stop = () => {
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            setIsPlaying(false);
        }
    };

    return [isPlaying, error, play, stop];
}